import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { QueryFetcher } from '@app/models/core/query-fetcher.model';
import { TaskTemplate } from '@app/models/tasks/task-template.model';
import { Task } from '@app/models/tasks/task.model';
import { DeprecatedTaskChecklistComponent } from '@app/modules/employees/components/task-checklist/deprecated/deprecated-task-checklist.component';
import { AssignTaskTemplatesService } from '@app/modules/employees/services';
import { AnalyticService, AuthService } from '@app/services';
import { IndexView } from '@common/views';
import { Employee } from '@models/employee/employee.model';

const RESULTS_PER_PAGE = 25;

const isScrollContainerAtBottom = (element: HTMLElement): boolean => {
    const itemHeight = element.querySelector('.task').clientHeight;
    return element.scrollTop + element.offsetHeight >= element.scrollHeight - itemHeight;
};
@Component({
    templateUrl: './offboarding-task-index.template.html',
    styleUrls: ['./offboarding-task-index.style.scss'],
})
export class OffboardingTaskIndexView extends IndexView implements OnInit {
    isLoading = true;

    isLoadingCompletedTasks = false;
    isLoadingIncompleteTasks = false;

    isAdmin = false;

    employee: Employee;

    tasks: Task[] = [];
    completedTasks: Task[] = [];
    incompleteTasks: Task[] = [];

    autoMatchedTaskTemplates: TaskTemplate[] = [];
    otherTaskTemplates: TaskTemplate[] = [];

    currentPageOfCompletedTasksContainer = 0;
    totalCompletedTasks: number;

    currentPageOfIncompleteTasksContainer = 0;
    totalIncompleteTasks: number;

    shouldConcat = false;
    events = AnalyticEvents;
    applyTaskEvent: AnalyticEvents;

    @ViewChild('applyTasksModal', { static: true }) applyTasksModal: DeprecatedModalComponent;
    @ViewChild('confirmDeleteDialog', { static: true }) confirmDeleteDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('incompleteTaskChecklist') incompleteTaskChecklist: DeprecatedTaskChecklistComponent;
    @ViewChild('completedTaskChecklist') completedTaskChecklist: DeprecatedTaskChecklistComponent;
    @ViewChild('incompleteTasksContainer') incompleteTasksContainer: ElementRef;
    @ViewChild('completedTasksContainer') completedTasksContainer: ElementRef;

    constructor(
        protected route: ActivatedRoute,
        private router: Router,
        private assignTasksService: AssignTaskTemplatesService,
        private auth: AuthService,
        private analytics: AnalyticService
    ) {
        super();
        this.isAdmin = this.auth.isAdmin();
        this.employee = this.route.snapshot.data.models.get('employee');
    }

    showApplyTasksModal(trackingEvent: AnalyticEvents): void {
        this.analytics.trackEvent(AnalyticEvents.OffboardingTaskApplyModal);
        this.applyTaskEvent = trackingEvent;
        this.applyTasksModal.show();
    }

    ngOnInit(): void {
        this.isLoading = true;
        Promise.all([
            this.getCompletedTasks(),
            this.getIncompleteTasks(),
            this.getAutoMatchedTaskTemplates(),
            this.getOtherTaskTemplates(),
        ])
            .then(() => {
                this.isLoading = false;
            })
            .catch(() => (this.isLoading = false));
    }

    addNewTask(): void {
        this.router.navigate(['new'], { relativeTo: this.route });
    }

    updateTasks(): void {
        this.currentPageOfCompletedTasksContainer = 0;
        this.currentPageOfIncompleteTasksContainer = 0;
        this.shouldConcat = false;

        Promise.all([this.getCompletedTasks(), this.getIncompleteTasks()]);
    }

    onApplyTaskTemplates(selectedTaskTemplates: TaskTemplate[]): void {
        this.isLoading = true;
        this.analytics.trackEvent(this.applyTaskEvent);
        this.assignTasksService
            .onSave(selectedTaskTemplates, [this.employee])
            .then(() => {
                this.updateTasks();
                this.isLoading = false;
            })
            .catch(() => (this.isLoading = false));
    }

    onCancel(): void {}

    onRemind(): void {
        this.analytics.trackEvent(AnalyticEvents.EmployeeProfileOffboardingTaskRemind);
    }

    onDelete(): void {
        this.analytics.trackEvent(AnalyticEvents.EmployeeProfileOffboardingTaskDeleted);
    }

    onCompletedTaskLoading(isLoading: boolean): void {
        this.isLoadingCompletedTasks = isLoading;
    }

    onIncompleteTaskLoading(isLoading: boolean): void {
        this.isLoadingIncompleteTasks = isLoading;
    }

    onScrollCompletedTasksList(): void {
        const element = this.completedTaskChecklist.container.nativeElement;

        const shouldLoadMoreTasks =
            this.completedTasks.length &&
            !this.isLoadingCompletedTasks &&
            isScrollContainerAtBottom(element) &&
            this.currentPageOfCompletedTasksContainer * RESULTS_PER_PAGE <= this.totalCompletedTasks;

        if (!shouldLoadMoreTasks) {
            return;
        }

        this.shouldConcat = true;
        this.getCompletedTasks();
    }

    onScrollIncompleteTasksList(): void {
        const element = this.incompleteTaskChecklist.container.nativeElement;

        const shouldLoadMoreTasks =
            this.incompleteTasks.length &&
            !this.isLoadingIncompleteTasks &&
            isScrollContainerAtBottom(element) &&
            this.currentPageOfIncompleteTasksContainer * RESULTS_PER_PAGE <= this.totalIncompleteTasks;

        if (!shouldLoadMoreTasks) {
            return;
        }

        this.shouldConcat = true;
        this.getIncompleteTasks();
    }

    private async getCompletedTasks(): Promise<void> {
        this.isLoadingCompletedTasks = true;
        const query = this.baseQuery.clone();

        query
            .where('isComplete', true)
            .page(this.currentPageOfCompletedTasksContainer + 1)
            .get()
            .then(([completedTasks, meta]) => {
                this.currentPageOfCompletedTasksContainer = meta.pagination.page;
                this.totalCompletedTasks = meta.pagination.total;

                this.completedTasks = this.shouldConcat ? [...this.completedTasks, ...completedTasks] : completedTasks;
                this.tasks = [...this.completedTasks, ...this.incompleteTasks];
                this.isLoadingCompletedTasks = false;
            });
    }

    private async getIncompleteTasks(): Promise<void> {
        this.isLoadingIncompleteTasks = true;
        const query = this.baseQuery.clone();

        query
            .where('isComplete', false)
            .page(this.currentPageOfIncompleteTasksContainer + 1)
            .get()
            .then(([incompleteTasks, meta]) => {
                this.currentPageOfIncompleteTasksContainer = meta.pagination.page;
                this.totalIncompleteTasks = meta.pagination.total;

                this.incompleteTasks = this.shouldConcat
                    ? [...this.incompleteTasks, ...incompleteTasks]
                    : incompleteTasks;
                this.tasks = [...this.completedTasks, ...this.incompleteTasks];
                this.isLoadingIncompleteTasks = false;
            });
    }

    private async getAutoMatchedTaskTemplates(): Promise<void> {
        const [autoMatchedTaskTemplates] = await TaskTemplate.where('task_type', 'offboarding')
            .where('filterMatching', this.employee.id)
            .get();
        this.autoMatchedTaskTemplates = autoMatchedTaskTemplates;
    }

    private async getOtherTaskTemplates(): Promise<void> {
        const [otherTaskTemplates] = await TaskTemplate.where('task_type', 'offboarding')
            .where('filterNonMatching', this.employee.id)
            .get();
        this.otherTaskTemplates = otherTaskTemplates;
    }

    private get baseQuery(): QueryFetcher {
        return Task.param('employee', this.employee.id)
            .where('task_type', 'offboarding')
            .where('subjectId', this.employee.id)
            .with(['assignee', 'file', 'subject', 'employeeDocument', 'completedBy']);
    }
}
